<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { format } from 'date-fns';
import {
  fetchCreditBalance,
  fetchCreditLedger,
} from 'custom/dashboard/extensions/billing/api';
import { formatBillingDateTime } from 'custom/dashboard/extensions/billing/helpers/formatBillingTime';
import { toAccountZonedTime } from 'custom/dashboard/helper/accountTimeZone';

const { t } = useI18n();
const store = useStore();

const fromDate = ref(toIsoDate(daysAgo(30)));
const toDate = ref(toIsoDate(new Date()));
const granularity = ref('day');
const loading = ref(false);
const error = ref('');
const balance = ref({});
const entries = ref([]);

const accountId = computed(() => store.state.route?.params?.accountId);
const isAdmin = computed(
  () => store.getters.getCurrentRole === 'administrator'
);
const consumedCredits = computed(() =>
  entries.value.reduce((total, entry) => total + Math.abs(entry.credits || 0), 0)
);
const cycleBucket = computed(() =>
  balance.value.buckets?.find(bucket => bucket.bucket_type === 'plan_cycle')
);
const purchasedBuckets = computed(
  () =>
    balance.value.buckets?.filter(bucket => bucket.bucket_type === 'purchased') ||
    []
);
const eventSummary = computed(() => {
  const grouped = new Map();
  entries.value.forEach(entry => {
    const key = entry.event_type || 'unknown';
    grouped.set(key, (grouped.get(key) || 0) + Math.abs(entry.credits || 0));
  });
  return [...grouped.entries()]
    .map(([eventType, credits]) => ({ eventType, credits }))
    .sort((a, b) => b.credits - a.credits);
});
const trendData = computed(() => {
  const grouped = new Map();
  entries.value.forEach(entry => {
    const key = trendBucket(entry.created_at);
    grouped.set(key, (grouped.get(key) || 0) + Math.abs(entry.credits || 0));
  });
  return [...grouped.entries()]
    .map(([period, credits]) => ({ period, credits }))
    .sort((a, b) => a.period.localeCompare(b.period));
});
const trendMaximum = computed(() =>
  Math.max(...trendData.value.map(row => row.credits), 0)
);

function daysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
}

function toIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const formatCredits = value =>
  new Intl.NumberFormat().format(Math.abs(Number(value) || 0));

const trendBucket = value => {
  const date = toAccountZonedTime(new Date(value));
  const pattern =
    granularity.value === 'year'
      ? 'yyyy'
      : granularity.value === 'month'
        ? 'yyyy-MM'
        : 'yyyy-MM-dd';
  return format(date, pattern);
};

const eventLabel = eventType => {
  const key = `CUSTOM.AI_USAGE.EVENTS.${eventType || 'unknown'}`;
  const translated = t(key);
  return translated === key ? eventType || '—' : translated;
};

const conversationUrl = conversationId =>
  `/app/accounts/${accountId.value}/conversations/${conversationId}`;

const load = async () => {
  if (!accountId.value) return;
  loading.value = true;
  error.value = '';
  try {
    const [balanceData, ledgerData] = await Promise.all([
      fetchCreditBalance(accountId.value),
      fetchCreditLedger(accountId.value, {
        entry_type: 'debit',
        from: fromDate.value,
        to: toDate.value,
      }),
    ]);
    balance.value = balanceData || {};
    entries.value = ledgerData || [];
  } catch (loadError) {
    error.value =
      loadError?.response?.data?.error || t('CUSTOM.COMMON.LOAD_FAILED');
  } finally {
    loading.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="h-full w-full overflow-y-auto">
    <div class="mx-auto max-w-6xl p-6 pb-16">
      <h1 class="mb-1 text-xl font-semibold text-n-slate-12">
        {{ t('CUSTOM.AI_USAGE.TITLE') }}
      </h1>
      <p class="mb-6 text-sm text-n-slate-10">
        {{ t('CUSTOM.AI_USAGE.SUBTITLE') }}
      </p>

      <div v-if="!isAdmin" class="text-sm text-n-ruby-9">
        {{ t('CUSTOM.COMMON.ADMIN_ONLY') }}
      </div>

      <template v-else>
        <div class="mb-4 flex flex-wrap items-end gap-3">
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('CUSTOM.AI_USAGE.FROM') }}
            <input
              v-model="fromDate"
              type="date"
              class="h-9 w-40 rounded border border-n-weak bg-n-alpha-1 px-2.5 text-sm text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('CUSTOM.AI_USAGE.TO') }}
            <input
              v-model="toDate"
              type="date"
              class="h-9 w-40 rounded border border-n-weak bg-n-alpha-1 px-2.5 text-sm text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('CUSTOM.AI_USAGE.GRANULARITY') }}
            <select
              v-model="granularity"
              class="h-9 w-32 rounded border border-n-weak bg-n-alpha-1 px-2.5 text-sm text-n-slate-12"
            >
              <option value="day">
                {{ t('CUSTOM.AI_USAGE.GRANULARITY_DAY') }}
              </option>
              <option value="month">
                {{ t('CUSTOM.AI_USAGE.GRANULARITY_MONTH') }}
              </option>
              <option value="year">
                {{ t('CUSTOM.AI_USAGE.GRANULARITY_YEAR') }}
              </option>
            </select>
          </label>
          <button
            type="button"
            class="h-9 rounded bg-n-brand px-4 text-sm text-white hover:opacity-90 disabled:opacity-60"
            :disabled="loading"
            @click="load"
          >
            {{
              loading
                ? t('CUSTOM.COMMON.QUERYING')
                : t('CUSTOM.COMMON.QUERY')
            }}
          </button>
        </div>

        <div v-if="error" class="mb-3 text-sm text-n-ruby-9">
          {{ error }}
        </div>

        <div class="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-4">
            <div class="text-xs text-n-slate-10">
              {{ t('CUSTOM.AI_USAGE.AVAILABLE_CREDITS') }}
            </div>
            <div class="mt-1 text-xl font-semibold text-n-slate-12">
              {{ formatCredits(balance.available_credits) }}
            </div>
          </div>
          <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-4">
            <div class="text-xs text-n-slate-10">
              {{ t('CUSTOM.AI_USAGE.CONSUMED_CREDITS') }}
            </div>
            <div class="mt-1 text-xl font-semibold text-n-slate-12">
              {{ formatCredits(consumedCredits) }}
            </div>
          </div>
          <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-4">
            <div class="text-xs text-n-slate-10">
              {{ t('CUSTOM.AI_USAGE.BILLABLE_EVENTS') }}
            </div>
            <div class="mt-1 text-xl font-semibold text-n-slate-12">
              {{ entries.length }}
            </div>
          </div>
          <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-4">
            <div class="text-xs text-n-slate-10">
              {{ t('CUSTOM.AI_USAGE.DEBT_CREDITS') }}
            </div>
            <div class="mt-1 text-xl font-semibold text-n-slate-12">
              {{ formatCredits(balance.debt_credits) }}
            </div>
          </div>
        </div>

        <section class="mb-8">
          <h2 class="mb-2 text-sm font-semibold text-n-slate-12">
            {{ t('CUSTOM.AI_USAGE.BALANCE_DETAIL') }}
          </h2>
          <div class="grid gap-3 sm:grid-cols-2">
            <div class="rounded-lg border border-n-weak p-3">
              <div class="text-sm font-medium text-n-slate-12">
                {{ t('CUSTOM.AI_USAGE.CURRENT_CYCLE') }}
              </div>
              <p v-if="cycleBucket" class="mt-1 text-xs text-n-slate-10">
                {{ formatCredits(cycleBucket.remaining) }} /
                {{ formatCredits(cycleBucket.allowance) }} ·
                {{
                  t('CUSTOM.AI_USAGE.EXPIRES', {
                    time: formatBillingDateTime(cycleBucket.expires_at),
                  })
                }}
              </p>
              <p v-else class="mt-1 text-xs text-n-slate-10">
                {{ t('CUSTOM.COMMON.NO_DATA') }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <div class="text-sm font-medium text-n-slate-12">
                {{ t('CUSTOM.AI_USAGE.PURCHASED_PACKS') }}
              </div>
              <p
                v-for="bucket in purchasedBuckets"
                :key="bucket.id"
                class="mt-1 text-xs text-n-slate-10"
              >
                {{ formatCredits(bucket.remaining) }} /
                {{ formatCredits(bucket.allowance) }} ·
                {{
                  t('CUSTOM.AI_USAGE.EXPIRES', {
                    time: formatBillingDateTime(bucket.expires_at),
                  })
                }}
              </p>
              <p
                v-if="!purchasedBuckets.length"
                class="mt-1 text-xs text-n-slate-10"
              >
                {{ t('CUSTOM.COMMON.NO_DATA') }}
              </p>
            </div>
          </div>
        </section>

        <section class="mb-8">
          <h2 class="mb-2 text-sm font-semibold text-n-slate-12">
            {{ t('CUSTOM.AI_USAGE.TREND') }}
          </h2>
          <div
            v-if="trendData.length"
            class="overflow-x-auto rounded-lg border border-n-weak p-4"
          >
            <div class="flex h-52 min-w-max items-end gap-3">
              <div
                v-for="row in trendData"
                :key="row.period"
                class="flex h-full w-14 flex-col items-center justify-end gap-2"
                :title="`${row.period}: ${formatCredits(row.credits)}`"
              >
                <span class="text-[10px] text-n-slate-10">
                  {{ formatCredits(row.credits) }}
                </span>
                <div
                  class="w-8 min-h-1 rounded-t bg-n-brand"
                  :style="{
                    height: `${Math.max(
                      2,
                      (row.credits / trendMaximum) * 100
                    )}%`,
                  }"
                />
                <span class="whitespace-nowrap text-[10px] text-n-slate-10">
                  {{ row.period }}
                </span>
              </div>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            {{ t('CUSTOM.COMMON.NO_DATA') }}
          </p>
        </section>

        <section class="mb-8">
          <h2 class="mb-2 text-sm font-semibold text-n-slate-12">
            {{ t('CUSTOM.AI_USAGE.BY_EVENT') }}
          </h2>
          <div
            v-if="eventSummary.length"
            class="overflow-hidden rounded-lg border border-n-weak"
          >
            <div
              v-for="row in eventSummary"
              :key="row.eventType"
              class="flex items-center justify-between border-b border-n-weak px-3 py-2 last:border-b-0"
            >
              <span class="text-sm text-n-slate-12">
                {{ eventLabel(row.eventType) }}
              </span>
              <span class="text-sm font-medium text-n-slate-12">
                {{ formatCredits(row.credits) }}
              </span>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            {{ t('CUSTOM.COMMON.NO_DATA') }}
          </p>
        </section>

        <section>
          <h2 class="mb-2 text-sm font-semibold text-n-slate-12">
            {{ t('CUSTOM.AI_USAGE.DETAIL') }}
          </h2>
          <div
            v-if="entries.length"
            class="overflow-x-auto rounded-lg border border-n-weak"
          >
            <table class="w-full text-sm">
              <thead class="bg-n-alpha-1 text-n-slate-11">
                <tr>
                  <th class="px-3 py-2 text-left font-medium">
                    {{ t('CUSTOM.AI_USAGE.TIME') }}
                  </th>
                  <th class="px-3 py-2 text-left font-medium">
                    {{ t('CUSTOM.AI_USAGE.EVENT') }}
                  </th>
                  <th class="px-3 py-2 text-left font-medium">
                    {{ t('CUSTOM.AI_USAGE.CONVERSATION') }}
                  </th>
                  <th class="px-3 py-2 text-right font-medium">
                    {{ t('CUSTOM.AI_USAGE.CREDITS_DEBITED') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="entry in entries"
                  :key="entry.id"
                  class="border-t border-n-weak"
                >
                  <td class="whitespace-nowrap px-3 py-2 text-n-slate-12">
                    {{ formatBillingDateTime(entry.created_at) }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-12">
                    {{ eventLabel(entry.event_type) }}
                  </td>
                  <td class="px-3 py-2">
                    <a
                      v-if="entry.conversation_id"
                      :href="conversationUrl(entry.conversation_id)"
                      class="text-n-brand hover:underline"
                    >
                      #{{ entry.conversation_id }}
                    </a>
                    <span v-else class="text-n-slate-9">—</span>
                  </td>
                  <td
                    class="whitespace-nowrap px-3 py-2 text-right font-medium text-n-slate-12"
                  >
                    −{{ formatCredits(entry.credits) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            {{ t('CUSTOM.COMMON.NO_DATA') }}
          </p>
        </section>
      </template>
    </div>
  </div>
</template>
